import { Badge, TooltipV2 } from '@cimplico/components';
import { useDispatch } from 'react-redux';
import { FYI_URL } from '../../config';
import { useIsActiveSubscription } from '../../hooks/use-is-active-subscription';
import { useFileRecord } from '../../queries/use-file-record';
import { useViewLink } from '../../queries/use-view-link';
import { setViewingDocument } from '../../slices/documentViewSlice';
import { FileRecordNew, FileRecordTypeEnum as TypeEnum } from '../../types/file-record';
import { isFolderType } from './file-utils';
import { ExclamationTriangleIcon } from '@heroicons/react/20/solid';
import ViewErrorDialog from './ViewErrorDialog';
import { useState } from 'react';
import FyiLogo from '../../assets/fyi.png';

type FileNameProps = {
    record: FileRecordNew;
    onFolderSelect?: (id: string) => void;
};

function isLegacyFYI(record: FileRecordNew): boolean {
    return (
        (record.provider !== 'FYI' && record.recordType === TypeEnum.FYI_DOCUMENT) ||
        (record.source?.provider !== 'FYI' && record.source?.recordType === TypeEnum.FYI_DOCUMENT)
    );
}

function FileName({ record, onFolderSelect }: FileNameProps): JSX.Element {
    const { recordType, source, link } = record;
    const isLink = link || source?.link;
    const isFYI = recordType === TypeEnum.FYI_DOCUMENT || source?.recordType === TypeEnum.FYI_DOCUMENT || record.provider === 'FYI';
    const isFolder = isFolderType(record.recordType);

    const documentViewQuery = useViewLink(record.id, false);
    const documentQuery = useFileRecord(record.source?.id, false);
    const dispatch = useDispatch();

    const [viewError, setViewError] = useState<FileRecordNew>();

    const activeSubscription = useIsActiveSubscription();

    function canClick(): boolean {
        if (activeSubscription || isFolder) {
            return true;
        }

        const mimeType = record?.mimeType || record.source?.mimeType;

        if (mimeType?.includes('pdf') || mimeType?.includes('png') || mimeType?.includes('jpg') || mimeType?.includes('jpeg')) {
            return true;
        }
        return false;
    }

    function handleClick(): void {
        if (isFolder) {
            onFolderSelect && onFolderSelect(record.id);
        } else {
            handleDocumentView();
        }
    }

    async function handleDocumentView(): Promise<void> {
        if (record.id) {
            dispatch(setViewingDocument({ record }));
        }
    }

    if (isLink) {
        return (
            <div className="flex items-center gap-2">
                <a
                    href={record.link || record.source?.link || undefined}
                    target="_blank"
                    className="font-medium text-indigo-600 dark:text-indigo-400 text-sm cursor-pointer text-left"
                    rel="noreferrer"
                >
                    {record.name}
                </a>
                {isFYI && <img src={FyiLogo} className="w-6 h-6 shrink-0" />}
            </div>
        );
    }

    return (
        <>
            {viewError && <ViewErrorDialog onClose={(): void => setViewError(undefined)} record={record} />}
            <button onClick={handleClick} disabled={!canClick() || documentViewQuery.isLoading || documentViewQuery.isFetching}>
                <div className="flex items-center gap-2">
                    <p className="font-medium text-indigo-600 dark:text-indigo-400 text-sm cursor-pointer text-left">{record.name}</p>
                    {isFYI && (
                        <span className="flex items-center">
                            <img src={FyiLogo} className="w-6 h-6 shrink-0" />
                            {isLegacyFYI(record) && (
                                <span className="h-fit block ml-1">
                                    <TooltipV2 message="This is a legacy file.">
                                        <ExclamationTriangleIcon className="h-4 w-4 text-yellow-500" />
                                    </TooltipV2>
                                </span>
                            )}
                        </span>
                    )}
                </div>
            </button>
        </>
    );
}

export default FileName;
